import React, { useState } from 'react';
import { ArrowLeft, Home, CheckCircle, XCircle, HelpCircle, BarChart2, Clock, RotateCcw, Eye, Bookmark, Download, Share2, Circle, ChevronUp, ChevronDown, Check, X } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { getSubjectGradient } from '../utils/constants';
interface TestResult {
  totalQuestions: number;
  answeredQuestions: number;
  correctAnswers: number;
  timeSpent: number;
  timeSpentFormatted: string;
  archivedQuestions: number[];
  questionResults: {
    id: number;
    status: 'correct' | 'incorrect' | 'unanswered';
    content: string;
  }[];
  selectedOptions?: Record<number, string>;
}
interface TestResultsScreenProps {
  subjectId: string;
  subjectTitle: string;
  topic: string;
  results: TestResult;
  onRetakeTest: () => void;
  onGoHome: () => void;
  onReviewQuestion: (questionId: number) => void;
}
export function TestResultsScreen({
  subjectId,
  subjectTitle,
  topic,
  results,
  onRetakeTest,
  onGoHome,
  onReviewQuestion
}: TestResultsScreenProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'questions'>('summary');
  const [expandedQuestions, setExpandedQuestions] = useState<Record<number, boolean>>({});
  const [selectedQuestion, setSelectedQuestion] = useState<null | {
    id: number;
    content: string;
  }>(null);
  // Function to get subject-specific placeholder image
  const getSubjectPlaceholder = (subjectId: string) => {
    switch (subjectId) {
      case 'matematik':
        return 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWF0aCUyMHByb2JsZW18ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
      case 'turkce':
        return 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Ym9va3MlMjBhbmQlMjBwZW58ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
      case 'fen':
        return 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c2NpZW5jZXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
      case 'inkılap':
        return 'https://images.unsplash.com/photo-1447069387593-a5de0862481e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aGlzdG9yeXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
      case 'ingilizce':
        return 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZW5nbGlzaHxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
      case 'din':
        return 'https://images.unsplash.com/photo-1585036156171-384164a8c675?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmVsaWdpb258ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
      default:
        return 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ZWR1Y2F0aW9ufGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60';
    }
  };
  // Calculate statistics
  const correctCount = results.correctAnswers;
  const incorrectCount = results.answeredQuestions - results.correctAnswers;
  const unansweredCount = results.totalQuestions - results.answeredQuestions;
  const successPercentage = Math.round(results.correctAnswers / results.totalQuestions * 100);
  // Get gradient based on subject
  const gradient = getSubjectGradient(subjectId);
  // Calculate performance level
  const getPerformanceLevel = () => {
    if (successPercentage >= 85) return {
      text: 'Mükemmel',
      color: 'text-green-400'
    };
    if (successPercentage >= 70) return {
      text: 'Çok İyi',
      color: 'text-blue-400'
    };
    if (successPercentage >= 50) return {
      text: 'İyi',
      color: 'text-yellow-400'
    };
    if (successPercentage >= 30) return {
      text: 'Geliştirilmeli',
      color: 'text-orange-400'
    };
    return {
      text: 'Yetersiz',
      color: 'text-red-400'
    };
  };
  const performance = getPerformanceLevel();
  // Toggle accordion for a question
  const toggleAccordion = (questionId: number) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };
  // Mock function to get question data for demo
  const getMockQuestionData = (questionId: number) => {
    // Generate a unique video URL for each question based on the question ID
    const videoUrls = ['https://www.youtube.com/embed/FkQaQZCzjic', 'https://www.youtube.com/embed/t4qLjYOQUXg', 'https://www.youtube.com/embed/HXV3zeQKqGY', 'https://www.youtube.com/embed/PkZNo7MFNFg', 'https://www.youtube.com/embed/rfscVS0vtbw', 'https://www.youtube.com/embed/eIrMbAQSU34' // Java tutorial
    ];
    // Use the question ID to select a video URL, ensuring each question gets a different video
    const videoIndex = questionId % videoUrls.length;
    return {
      content: `Bu ${questionId}. sorunun içeriği burada yer alacak. Soruda öğrenciden istenen...`,
      options: [{
        id: `q${questionId}_a`,
        text: `${questionId}. soru için A şıkkı`
      }, {
        id: `q${questionId}_b`,
        text: `${questionId}. soru için B şıkkı`
      }, {
        id: `q${questionId}_c`,
        text: `${questionId}. soru için C şıkkı`
      }, {
        id: `q${questionId}_d`,
        text: `${questionId}. soru için D şıkkı`
      }],
      correctOptionId: `q${questionId}_${['a', 'b', 'c', 'd'][Math.floor(Math.random() * 4)]}`,
      selectedOptionId: `q${questionId}_${['a', 'b', 'c', 'd'][Math.floor(Math.random() * 4)]}`,
      explanation: `Bu sorunun çözümü için ${questionId}. konudaki temel prensipleri uygulamanız gerekiyor...`,
      videoUrl: videoUrls[videoIndex],
      // Also provide a matching thumbnail for the video
      thumbnailUrl: `https://img.youtube.com/vi/${videoUrls[videoIndex].split('/').pop()}/hqdefault.jpg`
    };
  };
  // Mock data for demo
  const mockData = {
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    explanation: 'Bu sorunun çözümü için öncelikle...'
  };
  return <div className="space-y-6" data-id="element-1100">
      {/* Header */}
      <div className="text-center space-y-2" data-id="element-1101">
        <h1 className="text-3xl md:text-4xl font-bold" data-id="element-1102">Test Tamamlandı!</h1>
        <p className="text-xl text-gray-300" data-id="element-1103">
          {subjectTitle} - {topic}
        </p>
        <div className="flex justify-center items-center gap-2 mt-2" data-id="element-1104">
          <Clock className="w-5 h-5 text-purple-400" data-id="element-1105" />
          <span className="text-gray-300" data-id="element-1106">
            Toplam Süre: {results.timeSpentFormatted}
          </span>
        </div>
      </div>

      {/* Performance Banner */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1107">
        <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1108">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1109">
            <div className="flex-1 text-center md:text-left" data-id="element-1110">
              <h2 className="text-2xl font-bold mb-2" data-id="element-1111">
                Performans:{' '}
                <span className={performance.color} data-id="element-1112">{performance.text}</span>
              </h2>
              <p className="text-gray-300" data-id="element-1113">
                Bu testte toplam {results.totalQuestions} sorudan {correctCount}{' '}
                tanesini doğru cevapladın.
              </p>
            </div>
            <div className="w-32 h-32 relative" data-id="element-1114">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-1115">
                <circle cx="50" cy="50" r="45" fill="none" stroke="#1f1f1f" strokeWidth="10" data-id="element-1116" />
                <circle cx="50" cy="50" r="45" fill="none" stroke="url(#gradient)" strokeWidth="10" strokeDasharray={`${2 * Math.PI * 45 * successPercentage / 100} ${2 * Math.PI * 45 * (100 - successPercentage) / 100}`} strokeLinecap="round" data-id="element-1117" />
                <defs data-id="element-1118">
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1119">
                    <stop offset="0%" stopColor="#a855f7" data-id="element-1120" />
                    <stop offset="100%" stopColor="#6366f1" data-id="element-1121" />
                  </linearGradient>
                </defs>
                <text x="50" y="55" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="24" fontWeight="bold" data-id="element-1122">
                  {successPercentage}%
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1123">
        {/* Correct Answers Card */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-green-500 to-emerald-500" data-id="element-1124">
          <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1125">
            <div className="flex flex-col items-center justify-center space-y-2" data-id="element-1126">
              <CheckCircle className="w-8 h-8 text-green-500" data-id="element-1127" />
              <h3 className="text-lg font-medium" data-id="element-1128">Doğru</h3>
              <p className="text-3xl font-bold text-green-500" data-id="element-1129">
                {correctCount}
              </p>
            </div>
          </div>
        </div>

        {/* Incorrect Answers Card */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-red-500 to-rose-500" data-id="element-1130">
          <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1131">
            <div className="flex flex-col items-center justify-center space-y-2" data-id="element-1132">
              <XCircle className="w-8 h-8 text-red-500" data-id="element-1133" />
              <h3 className="text-lg font-medium" data-id="element-1134">Yanlış</h3>
              <p className="text-3xl font-bold text-red-500" data-id="element-1135">
                {incorrectCount}
              </p>
            </div>
          </div>
        </div>

        {/* Unanswered Questions Card */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-gray-500 to-slate-500" data-id="element-1136">
          <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1137">
            <div className="flex flex-col items-center justify-center space-y-2" data-id="element-1138">
              <Circle className="w-8 h-8 text-gray-500" data-id="element-1139" />
              <h3 className="text-lg font-medium" data-id="element-1140">Boş</h3>
              <p className="text-3xl font-bold text-gray-500" data-id="element-1141">
                {unansweredCount}
              </p>
            </div>
          </div>
        </div>

        {/* Time Spent Card */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-amber-500 to-yellow-500" data-id="element-1142">
          <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1143">
            <div className="flex flex-col items-center justify-center space-y-2" data-id="element-1144">
              <Clock className="w-8 h-8 text-amber-500" data-id="element-1145" />
              <h3 className="text-lg font-medium" data-id="element-1146">Süre</h3>
              <p className="text-3xl font-bold text-amber-500" data-id="element-1147">
                {Math.floor(results.timeSpent / 60)}:
                {(results.timeSpent % 60).toString().padStart(2, '0')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Review Answers Section */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1148">
        <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1149">
          <h2 className="text-xl font-bold mb-4" data-id="element-1150">Yanıtlarını Gözden Geçir</h2>
          <div className="space-y-2" data-id="element-1151">
            {results.questionResults.map((question, index) => {
            const isExpanded = expandedQuestions[question.id] || false;
            const mockData = getMockQuestionData(question.id);
            return <div key={question.id} className="overflow-hidden" data-id="element-1152">
                  <div className={`w-full flex items-center justify-between p-3 ${isExpanded ? 'bg-white/10 rounded-t-lg' : 'bg-white/5 hover:bg-white/10 rounded-lg'} transition-colors`} data-id="element-1153">
                    <div className="flex items-center gap-3" data-id="element-1154">
                      {question.status === 'correct' ? <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-1155">
                          <Check className="w-4 h-4 text-green-500" data-id="element-1156" />
                        </div> : question.status === 'incorrect' ? <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-1157">
                          <X className="w-4 h-4 text-red-500" data-id="element-1158" />
                        </div> : <div className="w-6 h-6 rounded-full bg-gray-500/20 flex items-center justify-center" data-id="element-1159">
                          <Circle className="w-4 h-4 text-gray-500" fill="none" data-id="element-1160" />
                        </div>}
                      <span className="font-medium" data-id="element-1161">Soru {index + 1}</span>
                    </div>
                    <div className="text-sm text-gray-400 line-clamp-1 text-left max-w-[70%]" data-id="element-1162">
                      {question.content}
                    </div>
                    <button onClick={() => toggleAccordion(question.id)} className="flex items-center gap-1 text-purple-400" data-id="element-1163">
                      <span data-id="element-1164">İncele</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" data-id="element-1165" /> : <ChevronDown className="w-4 h-4" data-id="element-1166" />}
                    </button>
                  </div>
                  {/* Accordion content */}
                  {isExpanded && <div className="bg-black/60 border border-white/10 rounded-b-lg p-4 space-y-4 animate-accordion-down" data-id="element-1167">
                      {/* Question content */}
                      <div className="prose prose-invert max-w-none" data-id="element-1168">
                        <p className="text-lg" data-id="element-1169">{mockData.content}</p>
                      </div>
                      {/* Options */}
                      <div className="space-y-2 mt-4" data-id="element-1170">
                        <h4 className="font-medium text-gray-300" data-id="element-1171">Şıklar:</h4>
                        {mockData.options.map(option => {
                    const isCorrect = option.id === mockData.correctOptionId;
                    const isSelected = option.id === mockData.selectedOptionId;
                    return <div key={option.id} className={`p-3 rounded-lg flex items-center gap-3 ${isCorrect ? 'bg-green-500/20 border border-green-500' : isSelected && !isCorrect ? 'bg-red-500/20 border border-red-500' : 'bg-white/5 border border-transparent'}`} data-id="element-1172">
                              <span className="flex-1" data-id="element-1173">{option.text}</span>
                              {isCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1174" />}
                              {isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-1175" />}
                            </div>;
                  })}
                      </div>
                      {/* Video solution - always show for every question with dynamic URL */}
                      <div className="mt-4" data-id="element-1176">
                        <h4 className="font-medium text-gray-300 mb-2" data-id="element-1177">
                          Video Çözüm:
                        </h4>
                        <VideoPlayer videoUrl={mockData.videoUrl} thumbnailUrl={mockData.thumbnailUrl || getSubjectPlaceholder(subjectId)} enableFullscreen={true} data-id="element-1178" />
                      </div>
                      {/* Explanation */}
                      {mockData.explanation && <div className="mt-4" data-id="element-1179">
                          <h4 className="font-medium text-gray-300" data-id="element-1180">
                            Açıklama:
                          </h4>
                          <div className="p-4 bg-white/5 rounded-lg text-gray-300 mt-2" data-id="element-1181">
                            {mockData.explanation}
                          </div>
                        </div>}
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Action Buttons - Only "Ana Sayfaya Dön" button, centered with updated styling */}
      <div className="flex justify-center" data-id="element-1182">
        <button onClick={onGoHome} className="flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg text-white hover:opacity-90 transition-colors" data-id="element-1183">
          <Home className="w-5 h-5" data-id="element-1184" />
          Ana Sayfaya Dön
        </button>
      </div>
    </div>;
}